import { ComponentProps, useEffect, useState } from 'react';
import { DayPicker, TZDate } from 'react-day-picker';
import 'react-day-picker/style.css';
import { DateRangePickerLayout } from '../DateRangePickerLayout';
import { getDayPickerLocale } from '../day-picker-locale';
import { sharedDayPickerStyles } from '../day-picker-styles';

/** Default: disable days after today (no future dates). */
const DEFAULT_DISABLED: ComponentProps<typeof DayPicker>['disabled'] = {
  after: new Date(),
};

export interface SingleDatePickerProps {
  open: boolean;
  onClose: () => void;
  /** Called with the selected date (start of day). Caller builds range if needed. */
  onChange: (date: Date) => void;
  /** Initial date for the picker. */
  initialDate?: Date | null;
  /** Which days are disabled in the calendar. Default: no future dates (after today). */
  disabled?: ComponentProps<typeof DayPicker>['disabled'];
  /** IANA time zone (e.g. "America/New_York") or UTC offset. When set, calendar uses TZDate so dates render in that zone. */
  tz?: string;
  /** App locale string (e.g. from useIntl().locale). Converted to DayPicker locale inside this component. */
  locale?: string;
}

/**
 * Single-date picker: click a day to apply. Calls onChange with the selected date (start of day).
 * When tz is provided, uses TZDate so the calendar and selection are in that time zone.
 * Renders inside Menu/Popover with no title or action buttons.
 */
export function SingleDatePicker({
  open,
  onClose,
  onChange,
  initialDate,
  disabled,
  tz,
  locale,
}: SingleDatePickerProps) {
  const effectiveDisabled = disabled ?? DEFAULT_DISABLED;
  const dayPickerLocale = locale != null ? getDayPickerLocale(locale) : undefined;

  const [selected, setSelected] = useState<Date | undefined>(() => {
    if (!initialDate) return undefined;
    return tz ? new TZDate(initialDate, tz) : new Date(initialDate);
  });

  useEffect(() => {
    if (open) {
      setSelected(
        initialDate ? (tz ? new TZDate(initialDate, tz) : new Date(initialDate)) : undefined,
      );
    }
  }, [open, initialDate, tz]);

  const applyDate = (date: Date | undefined) => {
    if (!date) {
      return;
    }

    onChange(date);
    onClose();
  };

  const defaultMonth = selected ?? (tz ? TZDate.tz(tz) : new Date());

  return (
    <DateRangePickerLayout
      onCancel={onClose}
      onApply={() => applyDate(selected)}
      applyDisabled={!selected}
      hideActions
    >
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={(date) => {
          setSelected(date ?? undefined);

          if (date) {
            applyDate(date);
          }
        }}
        defaultMonth={defaultMonth}
        disabled={effectiveDisabled}
        locale={dayPickerLocale}
        styles={sharedDayPickerStyles}
        timeZone={tz}
      />
    </DateRangePickerLayout>
  );
}
